/**
 * Validates if a timezone string is a valid IANA timezone.
 * Uses Intl.DateTimeFormat to validate.
 *
 * @param timezone - IANA timezone string (e.g., "America/New_York")
 * @returns true if valid, false otherwise
 */
export function isValidTimezone(timezone: string | null | undefined): boolean {
    if (!timezone) {
        return false;
    }

    try {
        Intl.DateTimeFormat(undefined, { timeZone: timezone });
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Converts a date to a specific timezone and returns formatted string.
 *
 * @param date - Date to convert
 * @param timezone - IANA timezone (e.g., "America/New_York")
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @param locale - Locale to use for formatting (e.g., "en-US", "en-GB"). Defaults to "en-US" for backward compatibility.
 * @returns Formatted date string in specified timezone
 */
export function formatDateInTimezone(
    date: Date | string | number,
    timezone: string,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    },
    locale = 'en-US'
): string {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

    return new Intl.DateTimeFormat(locale, {
        ...options,
        timeZone: timezone
    }).format(dateObj);
}

/**
 * Gets the timezone abbreviation (e.g., EST, PST) for a given timezone and date.
 *
 * @param timezone - IANA timezone (e.g., "America/New_York")
 * @param date - Date to get abbreviation for (defaults to now)
 * @param locale - Locale to use for formatting (e.g., "en-US", "en-GB"). Defaults to "en-US" for backward compatibility.
 * @returns Timezone abbreviation (e.g., "EST")
 */
export function getTimezoneAbbreviation(timezone: string, date: Date = new Date(), locale = 'en-US'): string {
    const formatter = new Intl.DateTimeFormat(locale, {
        timeZone: timezone,
        timeZoneName: 'short'
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');

    return timeZonePart?.value ?? '';
}
